import { useCallback, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import {
  AGENT_ALIASES,
  AGENT_NAMES,
  AgentType,
  type AgentNameType,
} from '../types/agents.js';

export const useAgents = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const fallbackAgent = useMemo<AgentType>(
    () => ({
      agentName: AGENT_NAMES.ASSISTANT,
      label: 'Assistant AI Agent',
      role: 'qualityAssurance',
      desc: t('Coordinates tasks across all agents.'),
    }),
    [t],
  );

  const mediaMappingAgent = useMemo<AgentType>(
    () => ({
      label: 'Media Mapping AI Agent',
      agentName: AGENT_NAMES.MEDIA_MAPPING,
      role: 'mediaBuyer',
      desc: t(
        'Matches media rows from an existing plan with active campaigns and provides a confidence score.',
      ),
      meta: {
        isVisibleInAgentWidget: false,
      },
    }),
    [t],
  );

  const voiceAgent = useMemo<AgentType>(
    () => ({
      label: 'Voice AI Agent',
      agentName: AGENT_NAMES.VOICE,
      role: 'voiceAgent',
      desc: t('Coordinates tasks across all agents.'),
    }),
    [t],
  );

  const agents: AgentType[] = useMemo(
    () => [
      {
        agentName: AGENT_NAMES.MEDIA_PLANNER,
        label: 'Planner AI Agent',
        role: 'mediaPlanner',
        desc: t(
          'Organizes data into actionable advertising plans to ensure campaign consistency and provides suggestions on platforms and budgets',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        agentName: AGENT_NAMES.MEDIA_STRATEGIST,
        label: 'Strategist AI Agent',
        role: 'mediaStrategist',
        desc: t(
          'Builds high-level strategies, identifies key audience clusters, and provides recommendations on channels and budgets.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Project Manager AI Agent',
        agentName: AGENT_NAMES.PROJECT_MANAGER,
        role: 'projectManager',
        desc: t('Coordinates tasks across all agents.'),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Supervisor AI Agent',
        agentName: AGENT_NAMES.SUPERVISOR,
        role: 'supervisor',
        desc: t('Oversees agents responses to ensure consistency.'),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Data Analyst AI Agent',
        agentName: AGENT_NAMES.DATA_ANALYST,
        role: 'dataAnalyst',
        desc: t(
          'Analyzes data and provides insights to help agents make informed decisions.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Reporting Specialist AI Agent',
        agentName: AGENT_NAMES.REPORT_BUILDER,
        role: 'reportingSpecialist',
        desc: t(
          'AI Agent that generates Word and Excel reports  from any conversation or custom prompt.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      voiceAgent,
      {
        label: 'Offline Planner AI Agent',
        agentName: AGENT_NAMES.OFFLINE_PLANER,
        role: 'offlinePlanner',
        desc: t(
          'AI agent that manages offline media planning activities and offer key recommendations.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Optimisation AI Agent',
        agentName: AGENT_NAMES.OPTIMISATION,
        role: 'mediaOptimization',
        desc: t(
          'AI Agent that deep-dives into your recommendations to explain, refine, accept or reject them.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      mediaMappingAgent,
    ],
    [t, voiceAgent, mediaMappingAgent],
  );

  const findAgentByName = useCallback(
    (agentName?: AgentNameType): AgentType | undefined => {
      if (!agentName) return;

      return agents.find(
        (a) =>
          a.agentName === agentName ||
          AGENT_ALIASES[a.agentName].includes(agentName),
      );
    },
    [agents],
  );

  const agentListWidgetData = agents.filter(
    (a) => a.meta?.isVisibleInAgentWidget,
  );

  return {
    agents,
    agentListWidgetData,
    fallbackAgent,
    voiceAgent,
    mediaMappingAgent,
    findAgentByName,
  };
};
